import React, { useEffect, useState } from 'react';
import Input from '@teetsh/app/src/components/Form/Input2';
import tw from 'twin.macro';
import Textarea from '../Textarea';

export type Size = '3xl' | '2xl' | 'xl' | 'lg' | 'md' | 'sm';

interface BaseProps {
  value: string;
  inputRef?: any;
  onConfirm: (v: string) => void;
  placeholder?: string;
  testId?: string;
  'aria-label'?: string;
  className?: string;
  autoFocus?: boolean;
  size?: Size;
  onBlur?: () => void;
  id?: string;
}

type TextareaProps = BaseProps &
  React.ComponentProps<typeof Textarea> & {
    textarea: true;
    blurOnEnter?: boolean;
  };

type InputProps = BaseProps &
  React.ComponentProps<typeof Input> & {
    textarea?: false;
    selectAllOnFocus?: boolean;
    autoSize?: boolean;
  };

export type Props = TextareaProps | InputProps;

export const textSize = {
  '3xl': tw`text-3xl`,
  '2xl': tw`text-2xl`,
  xl: tw`text-xl`,
  lg: tw`text-lg`,
  md: tw`text-base`,
  sm: tw`text-sm`,
};

export default function EditableTextComponent(props: Props) {
  const {
    placeholder,
    className,
    onConfirm,
    inputRef,
    onBlur,
    testId,
    size = 'md',
    autoFocus,
    id,
  } = props;
  const [value, setValue] = useState<string>(props.value);
  useEffect(() => {
    if (props.value != value) {
      setValue(props.value);
    }
  }, [props.value]);

  const commonProps = {
    value,
    id,
    inputRef,
    className,
    autoFocus,
    placeholder,
    'aria-label': props['aria-label'],
    css: [
      tw`w-full bg-transparent bg-opacity-0 border-opacity-0 hover:border-opacity-100 px-1 py-0.5`,
      textSize[size],
    ],
    onChange: e => setValue(e?.currentTarget?.value || ''),
    onBlur: () => {
      if (onBlur) {
        onBlur();
      }
      if (value !== props.value) {
        onConfirm(value);
      }
    },
  };

  if (props.textarea)
    return (
      <Textarea
        {...commonProps}
        onKeyDown={
          props.blurOnEnter
            ? (e: any) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                  (e.target as HTMLTextAreaElement).blur();
                }
              }
            : undefined
        }
        testId={testId}
      />
    );

  return (
    <Input
      {...commonProps}
      small={size === 'sm'}
      autoSize={props.autoSize ?? true}
      selectAllOnFocus={props.selectAllOnFocus}
      data-testid={testId}
    />
  );
}
